import React from "react";
import {
  useFloating,
  useInteractions,
  useClick,
  useDismiss,
  useRole,
  useId,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  offset,
  flip,
  shift,
  autoUpdate,
} from "@floating-ui/react";

interface PopoverProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placement?:
    | "top"
    | "top-start"
    | "top-end"
    | "bottom"
    | "bottom-start"
    | "bottom-end"
    | "left"
    | "left-start"
    | "left-end"
    | "right"
    | "right-start"
    | "right-end";
  title?: string;
  className?: string;
}

const Popover: React.FC<PopoverProps> = ({
  trigger,
  children,
  open,
  onOpenChange,
  placement = "bottom",
  title,
  className = "",
}) => {
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange,
    placement,
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const headingId = useId();
  const descriptionId = useId();

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()} className="">
        {trigger}
      </div>
      {open && (
        <FloatingPortal>
          <FloatingOverlay lockScroll>
            <FloatingFocusManager context={context}>
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                aria-labelledby={headingId}
                aria-describedby={descriptionId}
                {...getFloatingProps()}
                className={`bg-white rounded-lg shadow-xl border border-gray-300 p-6 text-[15px] text-black animate-fade-in ${className}`}
              >
                <div className="relative">
                  {title && (
                    <h3 id={headingId} className="text-lg font-bold mb-4">
                      {title}
                    </h3>
                  )}
                  <div id={descriptionId}>{children}</div>
                </div>
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </>
  );
};

export default Popover;
